import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/app/Header";
import Avatar from "../components/app/Avatar";
import AffiliateKitBox from "../components/app/AffiliateKitBox";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Heart, Pin, Send, Loader2, Flag, Sparkles, X
} from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function CommunityThread() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { type, id }
  const [kudosBusy, setKudosBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/community/threads/${id}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const postReply = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Log in to reply"); nav("/login"); return; }
    if (reply.trim().length < 1) return;
    setBusy(true);
    try {
      await api.post(`/community/threads/${id}/replies`, { body: reply });
      setReply("");
      await load();
      toast.success("Reply posted");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const toggleKudos = async () => {
    if (!user) { toast.info("Log in to send kudos"); nav("/login"); return; }
    setKudosBusy(true);
    try {
      await api.post(`/community/threads/${id}/kudos`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setKudosBusy(false); }
  };

  if (loading) return <div className="min-h-screen"><Header active="community" /><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div></div>;
  if (!data) return <div className="min-h-screen"><Header active="community" /><div className="p-20">Not found</div></div>;

  const { thread, replies } = data;
  const liked = user && (thread.kudos_by || []).includes(user.user_id);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="thread-page">
      <Header active="community" />

      <div className="max-w-[900px] mx-auto px-6 py-10">
        <Link to="/community" className="eyebrow flex items-center gap-2 mb-6 text-[#555] hover:text-[#ff4500]" data-testid="back-community">
          <ArrowLeft className="w-3 h-3" /> Back to community
        </Link>

        <div className="mb-6 p-4 border hairline bg-[#fffdfa] flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-[#ff4500] mt-1" strokeWidth={1.5} />
          <p className="text-xs text-[#555]">Be kind. Share useful detail. Personal attacks get removed.</p>
        </div>

        {/* Thread */}
        <article className="border hairline bg-white mb-8 overflow-hidden" data-testid="thread-body">
          {thread.cover_image && (
            <div
              className="w-full h-48 sm:h-60 lg:h-72 bg-[#1a1a1a] relative overflow-hidden"
              data-testid="thread-cover-image"
            >
              <img
                src={thread.cover_image}
                alt=""
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" aria-hidden="true" />
            </div>
          )}
          <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {thread.pinned && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#ff4500]">
                <Pin className="w-3 h-3" /> Pinned
              </span>
            )}
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border hairline mono">{thread.tag}</span>
            {thread.author_badge && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#ffefea] text-[#ff4500]">{thread.author_badge}</span>
            )}
          </div>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight mb-4">{thread.title}</h1>
          {thread.body_html ? (
            <div
              className="whitespace-pre-wrap text-[#222] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: thread.body_html }}
            />
          ) : (
            <p className="whitespace-pre-wrap text-[#222] leading-relaxed">{thread.body}</p>
          )}
          <div className="flex items-center justify-between mt-6 pt-4 border-t hairline">
            <div className="flex items-center gap-2 text-xs mono text-[#555]">
              <Avatar src={thread.author_picture} name={thread.user_name} size={28} testid="thread-author-avatar" />
              <span>{thread.user_name}</span>
              <span>·</span>
              <span>{fmt(thread.created_at)}</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={toggleKudos} disabled={kudosBusy}
                className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-1.5 border ${liked ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                data-testid="kudos-btn">
                <Heart className={`w-3.5 h-3.5 ${liked ? "fill-white" : ""}`} strokeWidth={1.5} />
                Kudos <span className="mono">{thread.kudos || 0}</span>
              </button>
              <button onClick={() => setReportTarget({ type: "thread", id: thread.thread_id })}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#555] hover:text-[#ff4500]"
                data-testid="report-thread">
                <Flag className="w-3.5 h-3.5" /> Report
              </button>
            </div>
          </div>
          </div>
        </article>

        {/* Replies */}
        <div className="mb-4 eyebrow">Replies ({replies.length})</div>
        <div className="space-y-3 mb-10">
          {replies.length === 0 && (
            <div className="p-5 border hairline bg-white text-sm text-[#888]">
              No replies yet — be the first to offer support.
            </div>
          )}
          {replies.map((r, i) => (
            <div key={r.reply_id} className="p-5 border hairline bg-white" data-testid={`reply-${i}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Avatar src={r.author_picture} name={r.user_name} size={28} testid={`reply-avatar-${i}`} />
                <span className="font-display text-base">{r.user_name}</span>
                {r.author_badge && (
                  <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[#ffefea] text-[#ff4500]">{r.author_badge}</span>
                )}
                <span className="mono text-[11px] text-[#888]">· {fmt(r.created_at)}</span>
                <button onClick={() => setReportTarget({ type: "reply", id: r.reply_id })}
                  className="ml-auto text-[11px] text-[#888] hover:text-[#ff4500] flex items-center gap-1"
                  data-testid={`report-reply-${i}`}>
                  <Flag className="w-3 h-3" /> Report
                </button>
              </div>
              {r.body_html ? (
                <div
                  className="whitespace-pre-wrap text-sm text-[#222] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: r.body_html }}
                />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-[#222] leading-relaxed">{r.body}</p>
              )}
            </div>
          ))}
        </div>

        {/* Reply form */}
        {user ? (
          <form onSubmit={postReply} className="p-5 border hairline bg-white" data-testid="reply-form">
            <div className="eyebrow mb-2">Add a supportive reply</div>
            <textarea
              className="cc-input h-40 sm:h-48 resize-y leading-relaxed"
              value={reply}
              onChange={(e) => setReply(e.target.value.slice(0, 2000))}
              placeholder="Keep it kind, specific and useful — exactly what you'd want to hear before a trip. Paragraph breaks render too, so feel free to write properly."
              maxLength={2000}
              data-testid="reply-body"
            />
            <div className="flex items-center justify-between mt-1 mb-2 text-[11px] mono">
              <span className={reply.trim().length > 0 && reply.trim().length < 1 ? "text-amber-600" : "text-[#888]"}>
                {reply.trim().length >= 150 ? "✓ this kind of reply is what makes the thread valuable" : reply.trim().length > 0 ? "✓ ready to post" : "Tell us your experience"}
              </span>
              <span className={reply.length > 1900 ? "text-amber-600 font-bold" : "text-[#888]"} data-testid="reply-body-count">
                {reply.length} / 2000
              </span>
            </div>
            <button className="btn-primary mt-1 inline-flex items-center gap-2" disabled={busy} data-testid="reply-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post reply
            </button>
          </form>
        ) : (
          <div className="p-5 border hairline bg-white text-sm text-[#555]">
            <Link to="/login" className="text-[#ff4500] underline" data-testid="login-to-reply">Log in</Link> to reply, send kudos, or report.
          </div>
        )}

        {/* iter67 — partner kit box visible on every thread */}
        <div className="mt-8">
          <AffiliateKitBox title="Kit our community trusts · partner deals" />
        </div>
      </div>

      {reportTarget && (
        <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />
      )}
    </div>
  );
}

function ReportModal({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) { toast.info("Give a reason (3+ chars)"); return; }
    setBusy(true);
    try {
      await api.post("/community/reports", { target_type: target.type, target_id: target.id, reason });
      toast.success("Thanks — our team will review.");
      onClose();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md border hairline-strong" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b hairline">
          <div>
            <div className="eyebrow">Report {target.type}</div>
            <div className="font-display text-xl">Keeping the campfire safe</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <textarea className="cc-input h-28" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="What's the issue? (e.g. harassment, spam, unsafe advice)" data-testid="report-reason" />
          <button className="btn-primary w-full" disabled={busy} data-testid="report-submit">
            {busy ? "Sending…" : "Submit report"}
          </button>
        </form>
      </div>
    </div>
  );
}
